import React, { Suspense } from "react";
import { useParams, Navigate } from "react-router-dom";

// Lazy-loaded case studies – only loaded when the route is visited
const MuseumOfBureaucracy = React.lazy(() =>
  import("./case-studies/MuseumOfBureaucracy")
);
const TheArtistAndThePervert = React.lazy(() =>
  import("./case-studies/TheArtistAndThePervert")
);
const IKEACinemaCatalogue = React.lazy(() =>
  import("./case-studies/IKEACinemaCatalogue")
);
const PorscheDreamjob = React.lazy(() =>
  import("./case-studies/PorscheDreamjob")
);
const BombsFromBerlin = React.lazy(() =>
  import("./case-studies/BombsFromBerlin")
);
const BundTimeIsRunningOut = React.lazy(() =>
  import("./case-studies/BundTimeIsRunningOut")
);
const DieParteiIDemo = React.lazy(() =>
  import("./case-studies/DieParteiIDemo")
);
const IKEADelivery = React.lazy(() => import("./case-studies/IKEADelivery"));
const FreshNFriends = React.lazy(() => import("./case-studies/FreshNFriends"));
const SiemensLaundryGallery = React.lazy(() =>
  import("./case-studies/SiemensLaundryGallery")
);
const BoschFreshStoneAgeMeat = React.lazy(() =>
  import("./case-studies/BoschFreshStoneAgeMeat")
);
const KaradaHouse = React.lazy(() => import("./case-studies/KaradaHouse"));
const KinoZeit = React.lazy(() => import("./case-studies/KinoZeit"));
const Projektil = React.lazy(() => import("./case-studies/Projektil"));

const caseStudies = {
  "museum-of-bureaucracy": MuseumOfBureaucracy,
  "the-artist-and-the-pervert": TheArtistAndThePervert,
  "ikea-cinema-catalogue": IKEACinemaCatalogue,
  "porsche-dreamjob": PorscheDreamjob,
  "bombs-from-berlin": BombsFromBerlin,
  "bund-time-is-running-out": BundTimeIsRunningOut,
  "die-partei-idemo": DieParteiIDemo,
  "ikea-delivery": IKEADelivery,
  "fresh-n-friends": FreshNFriends,
  "siemens-laundry-gallery": SiemensLaundryGallery,
  "bosch-fresh-stone-age-meat": BoschFreshStoneAgeMeat,
  "karada-house": KaradaHouse,
  "kino-zeit": KinoZeit,
  "projektil": Projektil,
};

// SEO content per case study — read by crawlers, invisible to users
const seoContent = {
  "the-artist-and-the-pervert": {
    title: "The Artist & The Pervert – Renée Gebhardt",
    description: "Feature documentary about composer Georg Friedrich Haas and performance artist Mollena Williams-Haas. Co-directed with Beatrice Behn. 40+ film festivals. Best Documentary Pornfilmfestival Berlin, CineKink NYC.",
    body: `Feature documentary. Co-directed with Beatrice Behn. One year of filming in their home, no script.
      Georg Friedrich Haas is one of the most important living composers. Mollena Williams-Haas is a BDSM educator and performance artist.
      They are married. We asked if we could film them. They said yes.
      40+ festival screenings including HotDocs Canada, Thessaloniki Doc Fest, Raindance.
      Best Documentary at Pornfilmfestival Berlin and CineKink NYC.
      Covered by FAZ Feuilleton, Deutschlandfunk, Siegessäule. Cited in academic research. Wikipedia entry.
      Art Direction & Co-Direction: Renée Gebhardt.`,
  },
  "ikea-cinema-catalogue": {
    title: "IKEA Cinema Catalogue – Renée Gebhardt",
    description: "Guerilla campaign that turned the IKEA catalogue into a cinematic experience. Screened as a short film in cinemas. 2× Cannes Silver, 1× Cannes Bronze.",
    body: `Guerilla campaign for IKEA Austria. Turned the IKEA catalogue into a short film screened in cinemas.
      Usually catalogue advertising is ignored. This one made people watch.
      2× Cannes Lions Silver, 1× Cannes Lions Bronze.
      Agency: DDB Berlin. Art Direction: Renée Gebhardt.`,
  },
  "museum-of-bureaucracy": {
    title: "Museum of Bureaucracy – Renée Gebhardt",
    description: "Exhibition and catalog design for a satirical museum about German bureaucracy. Commissioned by INSM and THJNK Berlin. 2× ADC Silver.",
    body: `Exhibition design and catalog for a satirical museum about German bureaucracy.
      Commissioned by INSM and THJNK Berlin.
      Typography walls, wayfinding system, and a publication so thorough it could probably file itself.
      The exhibition made bureaucracy feel physical — one person, infinite paragraphs, no exit.
      2× ADC Silver. Exhibition & Catalog Design: Renée Gebhardt.`,
  },
  "porsche-dreamjob": {
    title: "Porsche Dreamjob – Renée Gebhardt",
    description: "Recruitment campaign for Porsche. Targeted design and engineering talent through an unexpected creative approach.",
    body: `Recruitment campaign for Porsche AG.
      Targeted design and engineering talent through an unexpected creative approach.
      Art Direction: Renée Gebhardt.`,
  },
  "bombs-from-berlin": {
    title: "Bombs from Berlin – Renée Gebhardt",
    description: "PR campaign against German arms exports. Urban storytelling combining installation and media outreach.",
    body: `PR campaign against German arms exports.
      Urban storytelling combining street installation and media outreach.
      Made the abstract policy debate tangible on a human scale.
      Art Direction: Renée Gebhardt.`,
  },
  "bund-time-is-running-out": {
    title: "BUND Time is Running Out – Renée Gebhardt",
    description: "Environmental campaign for Germany's largest nature conservation organization BUND. Cannes Lions Gold.",
    body: `Environmental campaign for BUND — Germany's largest nature conservation organization.
      Addressed climate inaction through urgent visual storytelling.
      Cannes Lions Gold.
      Art Direction: Renée Gebhardt.`,
  },
  "die-partei-idemo": {
    title: "Die PARTEI iDemo – Renée Gebhardt",
    description: "Digital protest campaign for satirical German political party Die PARTEI. 4× ADC Gold.",
    body: `Campaign for the satirical German political party Die PARTEI.
      Digital protest action — the iDemo. Turned online participation into political statement.
      4× ADC Gold.
      Art Direction: Renée Gebhardt.`,
  },
  "ikea-delivery": {
    title: "IKEA Delivery – Renée Gebhardt",
    description: "Service campaign for IKEA Austria. 7× London International Awards.",
    body: `Service campaign for IKEA Austria highlighting the delivery offer.
      7× London International Awards (LIA).
      Agency: DDB Berlin. Art Direction: Renée Gebhardt.`,
  },
  "fresh-n-friends": {
    title: "Fresh'N'Friends – Renée Gebhardt",
    description: "Package design for a fresh food brand. Red Dot Grand Prix.",
    body: `Package design for Fresh'N'Friends, a fresh food brand.
      Red Dot Grand Prix — the highest distinction in the Red Dot Award.
      Design: Renée Gebhardt.`,
  },
  "siemens-laundry-gallery": {
    title: "Siemens Laundry Gallery – Renée Gebhardt",
    description: "Pop-up exhibition turning Siemens washing machines into an art gallery. Brought art into a retail context.",
    body: `Pop-up exhibition for Siemens Home Appliances.
      Turned washing machines into an art gallery. Brought contemporary art into a retail context.
      Exhibition Design & Art Direction: Renée Gebhardt.`,
  },
  "bosch-fresh-stone-age-meat": {
    title: "BOSCH Fresh Stone Age Meat – Renée Gebhardt",
    description: "Promotional campaign for Bosch home appliances. 5× Cannes Lions.",
    body: `Promotional campaign for Bosch home appliances.
      5× Cannes Lions.
      Art Direction: Renée Gebhardt.`,
  },
  "karada-house": {
    title: "Karada House – Renée Gebhardt",
    description: "Co-founded 2019. Queer art space in Berlin-Moabit, former church. 1,600+ events in six years. Full visual identity, photography, web design by Renée Gebhardt.",
    body: `Co-founded in 2019 with Caritia Abell and Beatrice Behn.
      Started in an abandoned stable in Prenzlauer Berg. Moved to a former church in Berlin-Moabit after eleven months of self-directed renovation.
      Queer kink and art space hosting Shibari workshops, exhibitions, conferences, and performances.
      1,600+ events in six years. Founded Queerope — the first international queer rope conference.
      Full visual identity, photography, web design, and all communications: Renée Gebhardt.
      Covered by Deutschlandfunk Nova, TAZ, Tagesspiegel, Siegessäule.`,
  },
  "kino-zeit": {
    title: "Kino-Zeit – Renée Gebhardt",
    description: "Complete rebrand of German cinema magazine Kino-Zeit. Editorial identity system covering typography, layout, and visual language.",
    body: `Complete rebrand of the German cinema magazine Kino-Zeit.
      Editorial identity system covering typography, layout, and visual language.
      Design & Art Direction: Renée Gebhardt.`,
  },
  "projektil": {
    title: "Projektil – Renée Gebhardt",
    description: "International lecture series at Bauhaus-Universität Weimar. Co-founded 2007. Stefan Sagmeister, Kurt Weidemann, Ruedi Baur, Amir Kassaei spoke there. ADC Junior Award, 3× 100 Beste Plakate. Still running after 20 years.",
    body: `International lecture series at Bauhaus-Universität Weimar. Co-founded in 2007 with Björn Kernspeckt and Sebastian Locke.
      Weekly lectures with multi-day workshops, bringing international speakers to Weimar.
      Speakers include Stefan Sagmeister, Kurt Weidemann, Ruedi Baur, Amir Kassaei, Eike König, Via Grafik, Dietmar Henneka.
      Full concept, art direction, corporate design, poster campaign, and web design: Renée Gebhardt.
      ADC Junior Award, 3× 100 Beste Plakate.
      Now an annual festival. Still running after 20 years.`,
  },
};

const hiddenStyle = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

function SEOBlock({ slug }) {
  const content = seoContent[slug];
  if (!content) return null;
  return (
    <div aria-hidden="true" style={hiddenStyle}>
      <h1>{content.title}</h1>
      <p>{content.description}</p>
      <p>{content.body}</p>
      <p>Art Director: Renée Gebhardt, Berlin. Freelance Art Director &amp; Filmmaker.</p>
    </div>
  );
}

function LoadingFallback() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          border: "2px solid #27272a",
          borderTop: "2px solid #fff",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const CaseStudyComponent = caseStudies[slug];

  if (!CaseStudyComponent) {
    return <Navigate to="/work" replace />;
  }

  return (
    <>
      <SEOBlock slug={slug} />
      <Suspense fallback={<LoadingFallback />}>
        <CaseStudyComponent />
      </Suspense>
    </>
  );
}